// Ubicación en el repo: src/pages/PublicSchedulingCancel.jsx
// Página pública de cancelación por token — /agendamiento-online/cancelar?token=...
// (spec-final-autoagendamiento.md sección 7, DECISIONS.md #74). Ruta
// pública, SIN sesión, montada fuera de RequireAuth/Layout en App.jsx —
// igual que PublicScheduling.jsx.
//
// Bug real que esto corrige: el link de cancelación del correo de
// confirmación ya apuntaba acá desde que se construyó public-agendamiento-create,
// pero la página nunca se había construido — con el catch-all de rutas de
// App.jsx, cualquiera que abriera el link caía en "/", que para un usuario
// con sesión activa (ej. Nicolás probando logueado) mostraba directamente
// el software interno. Grave porque el link llega a cualquier persona con
// acceso al correo, no solo al personal.
//
// Flujo (nunca cancela con el primer GET — sección 7 de la spec):
// 1. Al cargar, se resuelve el token del lado del servidor (acción
//    'lookup', solo lectura) y se muestran los datos reales de la hora.
// 2. Recién con un clic explícito en "Sí, cancelar mi hora" se llama a la
//    acción 'cancel', que ahí sí escribe.
import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { lookupCancellation, confirmCancellation } from '../lib/publicScheduling';
import { formatDateLong } from '../lib/format';

export default function PublicSchedulingCancel() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  // 'loading' | 'notfound' | 'disabled' | 'confirm' | 'cancelled' | 'kept' | 'error'
  const [phase, setPhase] = useState('loading');
  const [appointment, setAppointment] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setPhase('notfound');
      return;
    }
    let cancelled = false;
    lookupCancellation(token)
      .then((data) => {
        if (cancelled) return;
        // Interruptor "Agenda online" apagado (29-sep-2026, DECISIONS.md
        // #124) — cubre tanto un link nuevo como uno viejo que alguien
        // tenga guardado de antes de apagar la función.
        if (data.disabled) {
          setPhase('disabled');
          return;
        }
        if (!data.found) {
          setPhase('notfound');
          return;
        }
        setAppointment(data.appointment);
        setPhase(data.alreadyCancelled ? 'cancelled' : 'confirm');
      })
      .catch(() => {
        if (!cancelled) setPhase('notfound');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleCancelar() {
    setCancelling(true);
    setError('');
    try {
      const data = await confirmCancellation(token);
      // Defensa en profundidad: si el interruptor se apagó justo entre que
      // se cargó la página y este clic, el servidor ya lo rechaza igual
      // (ver public-agendamiento-cancel) — esto solo evita una pantalla en
      // blanco en ese caso puntual.
      if (data.disabled) {
        setPhase('disabled');
        return;
      }
      setAppointment(data.appointment);
      setPhase('cancelled');
    } catch (err) {
      setError(err.message || 'No se pudo cancelar la hora. Intenta de nuevo.');
    } finally {
      setCancelling(false);
    }
  }

  return (
    <div className="pubsched-page">
      <PublicTopbar />
      <div className="pubsched-wrap">
        <div className="pubsched-card pubsched-success-wrap">
          {phase === 'loading' && <div className="pubsched-loading">Buscando tu hora…</div>}

          {phase === 'notfound' && (
            <>
              <h2>No encontramos esta hora</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                Este link de cancelación no es válido o ya venció. Si necesitas ayuda, contáctanos directamente en tu sucursal.
              </p>
            </>
          )}

          {phase === 'disabled' && (
            <>
              <h2>Cancelación online no disponible</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                Por ahora no estamos procesando cancelaciones desde este link. Si necesitas cancelar o reagendar tu
                hora, contáctanos directamente en tu sucursal.
              </p>
            </>
          )}

          {phase === 'confirm' && appointment && (
            <>
              <h2>¿Cancelar tu hora?</h2>
              <div className="pubsched-summary-row">
                <span className="k">Atención</span>
                <span className="v">{appointment.type}</span>
              </div>
              <div className="pubsched-summary-row">
                <span className="k">Fecha</span>
                <span className="v">{appointment.dateLong || formatDateLong(appointment.date)}</span>
              </div>
              <div className="pubsched-summary-row">
                <span className="k">Hora</span>
                <span className="v">{appointment.time} hrs</span>
              </div>
              <div className="pubsched-summary-row">
                <span className="k">Sucursal</span>
                <span className="v">{appointment.branchName}</span>
              </div>
              {appointment.branchAddress && (
                <div className="pubsched-summary-row">
                  <span className="k">Dirección</span>
                  <span className="v">{appointment.branchAddress}</span>
                </div>
              )}

              <div className="pubsched-notice warn" style={{ marginTop: 12 }}>
                Esta acción libera tu hora para que otro paciente pueda tomarla. Si te arrepientes, tendrás que agendar una hora
                nueva.
              </div>

              {error && <div className="pubsched-notice danger">{error}</div>}

              <div className="pubsched-btn-row" style={{ justifyContent: 'center' }}>
                <button type="button" className="pubsched-btn-ghost" onClick={() => setPhase('kept')} disabled={cancelling}>
                  No, mantener mi hora
                </button>
                <button type="button" className="button-primary pubsched-btn-wide" onClick={handleCancelar} disabled={cancelling}>
                  {cancelling ? 'Cancelando…' : 'Sí, cancelar mi hora'}
                </button>
              </div>
            </>
          )}

          {phase === 'kept' && (
            <>
              <div className="pubsched-success-icon">✓</div>
              <h2>Tu hora sigue vigente</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>No hicimos ningún cambio. Nos vemos pronto.</p>
            </>
          )}

          {phase === 'cancelled' && appointment && (
            <>
              <div className="pubsched-success-icon">✓</div>
              <h2>Hora cancelada</h2>
              <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                Cancelamos tu {appointment.type?.toLowerCase()} del {appointment.dateLong || formatDateLong(appointment.date)} a
                las {appointment.time} hrs en {appointment.branchName}. Ya puedes cerrar esta página sin problema — si más
                adelante quieres agendar una hora nueva, puedes hacerlo cuando quieras.
              </p>
              <div className="pubsched-btn-row" style={{ justifyContent: 'center' }}>
                <Link to="/agendamiento-online" className="pubsched-btn-ghost">
                  Agendar una hora nueva
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Mismo header que la página pública institucional (`PublicLanding.jsx`) —
// reutiliza sus clases `.landing-header*` a propósito, para que ambas
// páginas públicas se vean consistentes (23-ago-2026, pedido de Nicolás:
// "para ambas debería ser la misma"). Sin botón de acceso: esta pantalla
// es solo para pacientes, "Acceso profesionales" no aplica acá.
function PublicTopbar() {
  return (
    <header className="landing-header">
      <div className="landing-header__brand">
        <strong>Agenda Comunal</strong>
        <span className="landing-header__sub">by GlobalVision</span>
      </div>
      <img src="/agendadock-logo.png" alt="AgendaDock" className="landing-header__powered-logo" />
    </header>
  );
}
